/** @format */

import { Button } from "antd";
import moment from "moment";
import React from "react";
import { TIntermittentCommunication } from "../../../../redux-modules/declarations/maps/intermittentCommunications";
import { AvatarPersona } from "../../ViewPresenze/partials/AvatarPersona";
import { fullName, isOvernight, nextStep, STATUS_META } from "../constants";
import { CLASSE_PASSO, etichettaGiorno, orario, persona, plurale } from "../format";
import { Elenco } from "../styles";
import { PillolaStato, PilloleTipologia } from "./Pillole";

interface Props {
  comunicazioni: TIntermittentCommunication[];
  oggi: string;
  aperta: number | null;
  /** testo quando l'elenco è vuoto, in base alla scheda */
  vuoto: { titolo: string; suggerimento: string };
  onApri: (id: number) => void;
}

/** La comunicazione richiede un intervento ed ha azioni disponibili: il pulsante diventa "Gestisci". */
export const daGestire = (c: TIntermittentCommunication): boolean =>
  c.available_actions.length > 0 && !!STATUS_META[c.status]?.needsAction;

/**
 * Elenco delle comunicazioni raggruppate per giorno del turno (l'ordine lo decide il backend),
 * con la stessa struttura della scheda "Da gestire" delle Presenze.
 */
export const ElencoComunicazioni = ({
  comunicazioni,
  oggi,
  aperta,
  vuoto,
  onApri,
}: Props): JSX.Element => {
  const giorni: { data: string; righe: TIntermittentCommunication[] }[] = [];
  comunicazioni.forEach(c => {
    const ultimo = giorni[giorni.length - 1];
    if (ultimo && ultimo.data === c.work_date) ultimo.righe.push(c);
    else giorni.push({ data: c.work_date, righe: [c] });
  });

  return (
    <Elenco data-cy="intermittent-communications-table">
      <div className="contenuto" role="table" aria-label="Comunicazioni">
        <div className="riga testata" role="row">
          <div role="columnheader">Dipendente</div>
          <div role="columnheader">Turno</div>
          <div role="columnheader">Sede</div>
          <div role="columnheader">Stato</div>
          <div role="columnheader">Prossimo passo</div>
          <div role="columnheader" style={{ textAlign: "right" }}>
            Azione
          </div>
        </div>

        {giorni.map(g => {
          const daFare = g.righe.filter(daGestire).length;
          return (
            <div key={g.data} role="rowgroup">
              <div className={`giorno${g.data === oggi ? " oggi" : ""}`}>
                {etichettaGiorno(g.data, oggi)}
                <span className="pillola grigio piccola">
                  {plurale(g.righe.length, "comunicazione", "comunicazioni")}
                </span>
                {daFare > 0 && (
                  <span className="pillola rosso piccola">
                    {plurale(daFare, "da gestire", "da gestire")}
                  </span>
                )}
              </div>
              {g.righe.map(c => (
                <Riga key={c.id} c={c} aperta={aperta === c.id} onApri={onApri} />
              ))}
            </div>
          );
        })}

        {!comunicazioni.length && (
          <div className="vuoto">
            <strong>{vuoto.titolo}</strong>
            <span className="attenuato">{vuoto.suggerimento}</span>
          </div>
        )}
      </div>
    </Elenco>
  );
};

const Riga = ({
  c,
  aperta,
  onApri,
}: {
  c: TIntermittentCommunication;
  aperta: boolean;
  onApri: (id: number) => void;
}): JSX.Element => {
  const passo = nextStep(c);
  const gestire = daGestire(c);
  const sedi = c.offices && c.offices.length ? c.offices.join(", ") : "–";

  return (
    <div
      className={`riga elemento${aperta ? " aperta" : ""}`}
      role="row"
      onClick={(): void => onApri(c.id)}
    >
      <div className="chi" role="cell">
        <AvatarPersona persona={persona(c)} />
        <span className="testi">
          <span className="nome">{fullName(c)}</span>
          <span className="sotto">
            <PilloleTipologia tipologie={c.worker_types} piccole />
            {c.fiscal_code ? (
              <span className="numeri">{c.fiscal_code}</span>
            ) : (
              <span className="mancante">Codice fiscale mancante</span>
            )}
          </span>
        </span>
      </div>
      <div className="turno" role="cell">
        <span className="orario">
          {orario(c)}
          {isOvernight(c) && (
            <span className="giorno-dopo" title="Finisce il giorno dopo">
              +1
            </span>
          )}
        </span>
      </div>
      <div className="sede" role="cell" title={sedi}>
        {sedi}
      </div>
      <div role="cell">
        <PillolaStato stato={c.status} etichetta={c.status_label} />
      </div>
      <div className={CLASSE_PASSO[passo.tone]} role="cell">
        {passo.text}
      </div>
      <div className="azione" role="cell">
        <Button
          size="small"
          type={gestire ? "primary" : "default"}
          onClick={(e): void => {
            e.stopPropagation();
            onApri(c.id);
          }}
          aria-label={`${gestire ? "Gestisci" : "Dettaglio"}: ${fullName(c)}, ${moment(
            c.work_date,
          ).format("dddd D MMMM")}`}
        >
          {gestire ? "Gestisci" : "Dettaglio"}
        </Button>
      </div>
    </div>
  );
};
